import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import {
  IconArrowBackUp, IconArrowLeft, IconDeviceFloppy, IconHistory, IconListDetails, IconPencil, IconRocket,
} from "@tabler/icons-react";
import { Alert, Badge, Button, Group, IconButton, Switch, Text } from "@/components/ui";
import { useCambiarActivoFlujo } from "../../hooks/use-notificaciones";
import type { FlujoDetalle } from "../../services/notificaciones-service";
import { BitacoraFlujoDialog } from "./BitacoraFlujoDialog";
import { DatosFlujoDialog } from "./DatosFlujoDialog";
import { VersionesDialog } from "./VersionesDialog";

interface Props {
  detalle: FlujoDetalle;
  sucio: boolean;
  guardando: boolean;
  publicando: boolean;
  erroresPublicacion: string[] | null;
  onCerrarErrores: () => void;
  onGuardar: () => void;
  onPublicar: () => void;
  onDescartar: () => void;
}

/** Barra superior del diseñador: estado del flujo y acciones de borrador/publicación. */
export function BarraDisenador({
  detalle, sucio, guardando, publicando, erroresPublicacion, onCerrarErrores, onGuardar, onPublicar, onDescartar,
}: Props) {
  const navigate = useNavigate();
  const { flujo, vigente } = detalle;
  const cambiarActivo = useCambiarActivoFlujo();
  const [dialogo, setDialogo] = useState<"datos" | "versiones" | "bitacora" | null>(null);

  return (
    <div className="border-b border-border">
      <div className="flex flex-wrap items-center gap-3 px-4 py-2.5">
        <IconButton variant="subtle" aria-label="Volver" title="Volver a notificaciones" onClick={() => void navigate({ to: "/admin/notificaciones" })}>
          <IconArrowLeft size={18} />
        </IconButton>
        <div className="min-w-0">
          <Group gap="xs" wrap="nowrap">
            <Text fw={600} truncate className="max-w-md">{flujo.nombre}</Text>
            <IconButton variant="subtle" size="sm" aria-label="Editar nombre" onClick={() => setDialogo("datos")}>
              <IconPencil size={14} />
            </IconButton>
          </Group>
          <Group gap="xs">
            {vigente ? <Badge color="exito" size="xs">Vigente v{vigente.numero}</Badge> : <Badge color="slate" size="xs">Sin publicar</Badge>}
            {sucio ? <Badge color="advertencia" size="xs">Cambios sin guardar</Badge> : null}
            {!sucio && vigente && flujo.borradorConCambios ? <Badge color="info" size="xs">Borrador distinto a lo publicado</Badge> : null}
          </Group>
        </div>

        <div className="ml-auto flex flex-wrap items-center gap-2">
          <Switch
            label={flujo.activo ? "Activo" : "Inactivo"}
            checked={flujo.activo}
            disabled={cambiarActivo.isPending}
            onCheckedChange={(activo) => cambiarActivo.mutate({ id: flujo.id, activo })}
          />
          <Button variant="default" size="sm" leftSection={<IconListDetails size={14} />} onClick={() => setDialogo("bitacora")}>
            Bitácora
          </Button>
          <Button variant="default" size="sm" leftSection={<IconHistory size={14} />} onClick={() => setDialogo("versiones")}>
            Versiones
          </Button>
          {sucio ? (
            <Button variant="default" size="sm" leftSection={<IconArrowBackUp size={14} />} onClick={onDescartar}>
              Descartar
            </Button>
          ) : null}
          <Button variant="outline" size="sm" leftSection={<IconDeviceFloppy size={14} />} disabled={!sucio} loading={guardando} onClick={onGuardar}>
            Guardar borrador
          </Button>
          <Button size="sm" leftSection={<IconRocket size={14} />} loading={publicando} onClick={onPublicar}>
            Publicar
          </Button>
        </div>
      </div>

      {erroresPublicacion ? (
        <div className="px-4 pb-3">
          <Alert variant="error" title="No se pudo publicar" onClose={onCerrarErrores}>
            <ul className="list-disc space-y-0.5 pl-5 text-sm">
              {erroresPublicacion.map((e) => <li key={e}>{e}</li>)}
            </ul>
          </Alert>
        </div>
      ) : null}

      <DatosFlujoDialog flujo={flujo} abierto={dialogo === "datos"} onCerrar={() => setDialogo(null)} />
      <VersionesDialog flujoId={flujo.id} abierto={dialogo === "versiones"} onCerrar={() => setDialogo(null)} />
      <BitacoraFlujoDialog flujoId={flujo.id} abierto={dialogo === "bitacora"} onCerrar={() => setDialogo(null)} />
    </div>
  );
}
